
import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { ApiUrl } from "../../../ApiUrl";

const App = ({ goToTab, documentDetails, setRelationDetailsInParent }) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [fileInputResetKey, setFileInputResetKey] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  const isFutureDate = (value) => {
    if (!value) return false;
    const enteredDate = new Date(value);
    if (Number.isNaN(enteredDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return enteredDate > today;
  };

  // Input field states
  const [formData, setFormData] = useState({
    srNo: "",
    documentType: "",
    documentNumber: "",
    documentFile: "",
    validFrom: "",
    validTo: "",
    enabled: false,
  });
  // const [relationDetails, setRelationDetails] = useState([]);

  useEffect(() => {
    if (documentDetails?.length > 0) {
      const mappedData = documentDetails.map((doc, index) => ({
        srNo: index + 1,
        documentType: doc.document_type_id,
        documentNumber: doc.document_number,
        documentFile: doc.document_file || doc.document_path || "",
        validFrom: doc.valid_from,
        validTo: doc.valid_to || doc.to_from,
        enabled: Boolean(doc.is_active),
      }));

      // In edit mode, show all existing uploaded documents in the table.
      setTableData(mappedData);

      // Keep input row empty for adding new document.
      setFormData({
        srNo: "",
        documentType: "",
        documentNumber: "",
        documentFile: "",
        validFrom: "",
        validTo: "",
        enabled: false,
      });
    } else {
      setTableData([]);
      setFormData({
        srNo: "",
        documentType: "",
        documentNumber: "",
        documentFile: "",
        validFrom: "",
        validTo: "",
        enabled: false,
      });
    }
    setFileInputResetKey((prev) => prev + 1);
  }, [documentDetails]);
  
  
  
  useEffect(() => {
    fetch(`${ApiUrl.apiurl}DOCUMENT/GetAllDocumentList/`)
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "Success") {
          const formattedOptions = data.data.map((doc) => ({
            value: doc.id,
            label: doc.documentdesc,
          }));
          setDocumentTypes(formattedOptions);
        }
      })
      .catch((error) => {
        console.error("Error fetching document types:", error);
      });
  }, []);

  useEffect(() => {
    const convertFileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    };

    const persistDocumentsDraft = async () => {
      try {
        const formattedDocuments = await Promise.all(
          tableData.map(async (doc) => {
            if (!doc.documentType || !doc.documentNumber) {
              return null;
            }

            let base64File = "";

            if (typeof doc.documentFile === "string") {
              base64File = doc.documentFile;
            } else if (doc.documentFile) {
              base64File = await convertFileToBase64(doc.documentFile);
            }

            return {
              document_type_id: Number(doc.documentType),
              document_number: doc.documentNumber,
              document_file: base64File,
              valid_from: doc.validFrom,
              valid_to: doc.validTo,
              enabled: doc.enabled,
            };
          })
        );

        sessionStorage.setItem(
          "staff_registration_documents",
          JSON.stringify(formattedDocuments.filter(Boolean))
        );
      } catch (error) {
        console.error("Error while saving document draft:", error);
      }
    };

    persistDocumentsDraft();
  }, [formData, tableData]);



  const handleNextClick = async () => {
    try {
      const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      const formattedDocuments = await Promise.all(
        tableData.map(async (doc) => {
          if (!doc.documentType || !doc.documentNumber) {
            return null;
          }

          let base64File = "";

          if (typeof doc.documentFile === "string") {
            base64File = doc.documentFile;
          } else if (doc.documentFile) {
            base64File = await convertFileToBase64(doc.documentFile);
          }

          return {
            document_type_id: Number(doc.documentType),
            document_number: doc.documentNumber,
            document_file: base64File,
            valid_from: doc.validFrom,
            valid_to: doc.validTo,
            enabled: doc.enabled,
          };
        })
      );

      sessionStorage.setItem(
        "staff_registration_documents",
        JSON.stringify(formattedDocuments.filter(Boolean))
      );
      goToTab(3);
    } catch (error) {
      console.error("Error in handleNextClick:", error);
      alert("An error occurred: " + error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, type, value, checked, files } = e.target;
    const updatedValue =
      type === "checkbox" ? checked : type === "file" ? files[0] : value;

    if (name === "validFrom" || name === "validTo") {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: isFutureDate(value)
          ? "This date cannot be in the future."
          : "",
      }));
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: updatedValue,
    }));
  };

  const handleAddRow = () => {
    if (!formData.documentType || !formData.documentNumber) {
      alert("Please select Document Type and enter Document Number before adding.");
      return;
    }

    if (!formData.validFrom || !formData.validTo) {
      alert("Please enter both Valid From and Valid To dates.");
      return;
    }

    if (isFutureDate(formData.validFrom) || isFutureDate(formData.validTo)) {
      setValidationErrors({
        validFrom: isFutureDate(formData.validFrom)
          ? "This date cannot be in the future."
          : "",
        validTo: isFutureDate(formData.validTo)
          ? "This date cannot be in the future."
          : "",
      });
      return;
    }

    if (new Date(formData.validTo) < new Date(formData.validFrom)) {
      alert("Valid To cannot be earlier than Valid From.");
      return;
    }

    const newRow = { ...formData, srNo: tableData.length + 1 };

    setTableData([...tableData, newRow]); // Add at top
    // Clear input fields
    setFormData({
      srNo: "",
      documentType: "",
      documentNumber: "",
      documentFile: "",
      validFrom: "",
      validTo: "",
      enabled: false,
    });
    setFileInputResetKey((prev) => prev + 1);
    setValidationErrors({});
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Sr.No</th>
                <th>Document Type</th>
                <th>Document Number</th>
                <th>Document</th>
                <th>Valid From</th>
                <th>Valid To</th>
                <th>Enabled</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {/* Existing added rows */}
              {tableData.map((row, index) => (
                <tr key={index}>
                  <td>{row.srNo}</td>
                  <td>
                    {
                      documentTypes.find(
                        (doc) =>
                          doc.value.toString() === row.documentType.toString()
                      )?.label
                    }
                  </td>
                  <td>{row.documentNumber}</td>
                  <td>
                    {typeof row.documentFile === "string" ? (
                      <a
                        href={row.documentFile}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.documentFile.split("/").pop()}
                      </a>
                    ) : (
                      row.documentFile?.name
                    )}
                  </td>

                  <td>{row.validFrom}</td>
                  <td>{row.validTo}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      disabled
                      className="form-check-input"
                    />
                  </td>
                  <td></td>
                </tr>
              ))}

              {/* Input row */}
              <tr>
                <td>{tableData.length + 1}</td>

                <td>
                  <select
                    className="form-select"
                    name="documentType"
                    value={formData.documentType}
                    onChange={handleInputChange}
                  >
                    <option value="">Select</option>
                    {documentTypes.map((doc) => (
                      <option key={doc.value} value={doc.value}>
                        {doc.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    name="documentNumber"
                    value={formData.documentNumber}
                    onChange={handleInputChange}
                  />
                </td>
                <td>
                  <input
                    key={fileInputResetKey}
                    type="file"
                    className="form-control"
                    name="documentFile"
                    onChange={handleInputChange}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    className={`form-control ${validationErrors.validFrom ? "is-invalid" : ""}`}
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleInputChange}
                  />
                  {validationErrors.validFrom && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.validFrom}
                    </div>
                  )}
                </td>
                <td>
                  <input
                    type="date"
                    className={`form-control ${validationErrors.validTo ? "is-invalid" : ""}`}
                    name="validTo"
                    value={formData.validTo}
                    onChange={handleInputChange}
                  />
                  {validationErrors.validTo && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.validTo}
                    </div>
                  )}
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name="enabled"
                    checked={formData.enabled}
                    onChange={handleInputChange}
                  />
                </td>
                <td>
                  <button className="btn btn-primary" onClick={handleAddRow}>
                    Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="d-flex justify-content-between mb-3 mt-2"></div>
    </div>
  );
};

export default App;